import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Smartphone, CheckCircle2, XCircle, Loader2, FlaskConical } from "lucide-react";
import { formatCurrency, API_URL } from "../../lib/utils";

// Acompanha uma cobrança AppyPay (Multicaixa Express) até ser confirmada/recusada
export const AppyPayWaitDialog = ({ charge, headers, onSuccess, onClose, title = "Multicaixa Express" }) => {
  const [state, setState] = useState(charge);
  const [seconds, setSeconds] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    setState(charge);
    setSeconds(0);
    if (!charge) return;
    const tick = async () => {
      try {
        const r = await axios.get(`${API_URL}/payments/appypay/charges/${charge.charge_id}`, { headers, withCredentials: true });
        setState(r.data);
        if (r.data.status === "success") {
          clearInterval(timer.current);
          onSuccess?.(r.data);
        } else if (r.data.status !== "pending") {
          clearInterval(timer.current);
        }
      } catch { /* tenta novamente no próximo ciclo */ }
    };
    timer.current = setInterval(() => { setSeconds((s) => s + 3); tick(); }, 3000);
    return () => clearInterval(timer.current);
  }, [charge?.charge_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async () => {
    try { await axios.post(`${API_URL}/payments/appypay/charges/${charge.charge_id}/cancel`, {}, { headers, withCredentials: true }); } catch { /* já não está pendente */ }
    onClose();
  };
  const simulate = async () => {
    try {
      const r = await axios.post(`${API_URL}/payments/appypay/charges/${charge.charge_id}/simulate`, {}, { headers, withCredentials: true });
      setState(r.data);
      if (r.data.status === "success") { clearInterval(timer.current); onSuccess?.(r.data); }
    } catch { /* ignorar */ }
  };

  if (!charge) return null;
  const pending = state?.status === "pending";
  const failed = state && !["pending", "success"].includes(state.status);
  const phone = state?.payment_info?.phoneNumber || "";

  return (
    <Dialog open={!!charge} onOpenChange={(o) => !o && !pending && onClose()}>
      <DialogContent className="max-w-sm" data-testid="appypay-wait-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-emerald-500" /> {title}</DialogTitle>
          <DialogDescription>
            {state?.simulated && <span className="inline-flex items-center gap-1 text-amber-500 font-bold text-xs mr-1" data-testid="appypay-simulated-badge"><FlaskConical className="w-3 h-3" /> SIMULADO</span>}
            Pedido de pagamento de <strong className="font-mono">{formatCurrency(state?.amount)}</strong> enviado para <strong>{phone.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 flex flex-col items-center text-center gap-3">
          {pending && (
            <>
              <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
              <p className="text-sm" data-testid="appypay-wait-status">A aguardar aprovação do cliente na app Multicaixa Express… <span className="font-mono text-muted-foreground">{seconds}s</span></p>
              <p className="text-xs text-muted-foreground">O POS confirma sozinho assim que a AppyPay validar o pagamento.</p>
            </>
          )}
          {state?.status === "success" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-emerald-500" />
              <p className="font-semibold text-emerald-500" data-testid="appypay-wait-status">Pagamento confirmado</p>
            </>
          )}
          {failed && (
            <>
              <XCircle className="w-12 h-12 text-red-500" />
              <p className="font-semibold text-red-500" data-testid="appypay-wait-status">{state.status === "cancelled" ? "Cobrança cancelada" : "Pagamento não concluído"}</p>
              {state.provider_message && <p className="text-xs text-muted-foreground">{state.provider_message}</p>}
            </>
          )}
        </div>
        <DialogFooter className="gap-2">
          {pending && (state?.simulated || state?.env === "sandbox") && (
            <Button variant="outline" onClick={simulate} data-testid="appypay-simulate-btn"><FlaskConical className="w-4 h-4 mr-1.5" /> Simular aprovação</Button>
          )}
          {pending ? (
            <Button variant="ghost" onClick={cancel} data-testid="appypay-cancel-btn">Cancelar pedido</Button>
          ) : (
            <Button onClick={onClose} data-testid="appypay-close-btn">{failed ? "Tentar outro método" : "Fechar"}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
